import React, { useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { useAppContext } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import { TrackingLocation, impression } from '@tracking';

import ForYouFeaturedContentFeed from './FeaturedContentFeed';
import ForYouSection from './Section';
import { FOR_YOU_ENGAGEMENT_LABELS, FOR_YOU_SECTION_IDS } from './constants';
import type { ForYouBookmarksProps } from './interfaces';
import useRegisterSection from './useRegisterSection';

const ForYouBookmarks = ({ bookmarks }: ForYouBookmarksProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.Bookmarks,
  });
  const { isApp3_0OrGreater } = useNativeAppVersionCheck();
  const { initialCurrentUser, showBookmarksFlagEnabled } = useAppContext();

  const showBookmarks =
    initialCurrentUser?.is_subscriber && (isApp3_0OrGreater || showBookmarksFlagEnabled);

  const featuredContents = useMemo(() => {
    return bookmarks.map(({ content }) => content);
  }, [bookmarks]);

  useRegisterSection(FOR_YOU_SECTION_IDS.Bookmarks, featuredContents?.length > 0);

  if (featuredContents?.length === 0 || !showBookmarks) return null;

  const bookmarksDisplayText = FOR_YOU_ENGAGEMENT_LABELS.Subscribed.Bookmarks;

  return (
    <ForYouSection
      headerLink="/bookmarks"
      headerLinkText="View all bookmarks"
      id={FOR_YOU_SECTION_IDS.Bookmarks}
      title={bookmarksDisplayText.title}
    >
      <Track>
        <InView
          onChange={(inView) => {
            inView && trackEvent(impression({ impressionElement: FOR_YOU_SECTION_IDS.Bookmarks }));
          }}
          triggerOnce
        >
          <ForYouFeaturedContentFeed contents={featuredContents} />
        </InView>
      </Track>
    </ForYouSection>
  );
};

export default ForYouBookmarks;
